import settings from '@/settings'
import { LOCALE } from '@/helpers/locale'

export const detectIE = () => {
  const windowNavigator = window.navigator.userAgent.toLowerCase()
  return (/trident/gi).test(windowNavigator) || (/msie/gi).test(windowNavigator)
}

export const detectSafari = () => {
  const windowNavigator = window.navigator.userAgent.toLowerCase()
  return (/^((?!chrome|android).)*safari/gi).test(windowNavigator)
}

export const detectUserAgentBot = () => {
  const windowNavigator = window.navigator.userAgent.toLowerCase()
  return (/bot|googlebot|crawler|spider|robot|crawling/i).test(windowNavigator)
}

export const parseEncodedString = (encodedString) => {
  const el = document.createElement('span')
  el.innerHTML = encodedString
  return el.innerText
}

export const getSortingKeys = (items) => {
  return items.map(el => el.fields.key)
    .reduce((sum, cur, idx) => {
      sum[cur] = idx
      return sum
    }, {})
}

export const setWindowWebpBrowserSupportFlag = () => {
  const img = new Image()
  img.onload = function () {
    var result = (img.width > 0) && (img.height > 0)
    window.isWebpSupported = result
  }
  img.onerror = function () {
    window.isWebpSupported = false
  }
  img.src = 'data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA'
}

export const checkIsElementInView = (el, percentVisible = 80) => {
  const rect = el?.getBoundingClientRect()
  if (!rect) return false
  const windowHeight = (window.innerHeight || document.documentElement.clientHeight)

  return !(
    Math.floor(100 - (((rect.top >= 0 ? 0 : rect.top) / +-rect.height) * 100)) < percentVisible ||
    Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) < percentVisible
  )
}

export const constructExternalPDPLink = (sku, locale = LOCALE.en.code) => {
  return `https://www.hubbell.com/${settings.brandNamesByCountry[settings.app.country]}/${locale}/p/${sku}`
}

export const forceReload = () => {
  window.location.reload(true)
}

/**
 * Extracts localized data from a Contentful JSON structure.
 *
 * @param {object} data The raw data object with locale-keyed fields.
 * @param {string} locale The desired locale (e.g., 'fr-CA').
 * @param {string} [fallbackLocale='en-US'] The fallback locale.
 * @returns {object} A new object with values for the specified locale.
 */
export const getLocalizedData = (data, locale, fallbackLocale = LOCALE.en.iso) => {
  // Base case for recursion: if data is not a plain object (e.g., it's a primitive or an array), it cannot be localized further, so return it as is.
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return data
  }

  const result = {}

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value = data[key]
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        if (value[locale] !== undefined) {
          result[key] = getLocalizedData(value[locale], locale, fallbackLocale)
        } else if (value[fallbackLocale] !== undefined) {
          result[key] = getLocalizedData(value[fallbackLocale], locale, fallbackLocale)
        } else {
          // This handles nested objects that don't have locale keys themselves
          // but are children of a locale-keyed object (like 'sys' or 'fields').
          const localizedValue = value[locale] ?? value[fallbackLocale]
          if (localizedValue !== undefined) {
            result[key] = (typeof localizedValue === 'object' && localizedValue !== null)
              ? getLocalizedData(localizedValue, locale, fallbackLocale)
              : localizedValue
          } else {
            result[key] = getLocalizedData(value, locale, fallbackLocale)
          }
        }
      } else {
        result[key] = value
      }
    }
  }

  // After processing all keys, check if the result is a locale-keyed object
  if (result[locale] !== undefined) {
    return result[locale]
  }
  if (result[fallbackLocale] !== undefined) {
    return result[fallbackLocale]
  }

  return result
}
